import { useMemo } from 'react'

import { AgChartOptions } from 'ag-charts-community'

import { cn } from '@fortem-monorepo/js-utilities'
import { CustomAgChart } from '@fortem-monorepo/react-ag'
import { ICReport } from '@fortem-monorepo/web-sdk'

import { perIssuerItemStyler } from '@/pages/funds/ic-report/components/primitives/charts/per-issuer-styler'
import { ICReportChartPrimitive } from '@/pages/funds/ic-report/components/primitives/types'
import { income } from '@/react-query/queries/ic-report/hooks'

type PerIssuerWgtData = ICReport.GetPerIssuerData['per_issuer_wgt']
type PerIssuerWgtItem = PerIssuerWgtData[string][number]

export interface PerIssuerWgtChartsProps extends ICReportChartPrimitive {}

export const PerIssuerWgtCharts: React.FC<PerIssuerWgtChartsProps> = ({ params, options }) => {
  const query = income.usePerIssuer(params)
  const data = query.data

  const charts = Object.entries(data?.per_issuer_wgt ?? {})

  return (
    <div
      className={cn('grid min-h-40', {
        ['grid-cols-2']: charts.length === 2,
      })}
    >
      {charts.map(([key, values]) => {
        return <PerIssuerWgtChart title={key} data={values} options={options} />
      })}
    </div>
  )
}

interface PerIssuerWgtChartProps {
  title: string
  data: ICReport.GetPerIssuerData['per_issuer_wgt'][string]
  options: ICReportChartPrimitive['options']
}

export const PerIssuerWgtChart: React.FC<PerIssuerWgtChartProps> = ({ title, data, options }) => {
  const formattedData = useMemo(() => {
    return data.map((item) => {
      return {
        ...item,
        wgt_locked_ytm: parseFloat((item.wgt_locked_ytm * 100).toFixed(2)),
      }
    })
  }, [data])

  const mergedOptions: AgChartOptions<PerIssuerWgtItem> = {
    data: formattedData,
    title: {
      text: `${title} Yield (%)`,
    },
    series: [
      {
        type: 'pie',
        angleKey: 'wgt_locked_ytm',
        legendItemKey: 'product_issuer',
        calloutLabelKey: 'wgt_locked_ytm',
        calloutLabel: {
          formatter: (data) => {
            return `${data.value}%`
          },
        },
        itemStyler: perIssuerItemStyler,
        tooltip: {
          renderer: (data) => {
            return {
              data: [
                {
                  label: data.datum.product_issuer,
                  value: `${data.datum.wgt_locked_ytm}%`,
                },
              ],
            }
          },
        },
      },
    ],
    ...options,
  }

  return <CustomAgChart options={mergedOptions} />
}
